import { useState } from 'react';
import { X, Copy, Check, Brain } from 'lucide-react';
import { AnalysisItem } from '../types';

interface AIPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: AnalysisItem;
  analysisTitle: string;
  analysisDescription: string;
  plusListTitle?: string | null;
  minusListTitle?: string | null;
}

export default function AIPromptModal({ isOpen, onClose, item, analysisTitle, analysisDescription, plusListTitle, minusListTitle }: AIPromptModalProps) {
  const [isCopied, setIsCopied] = useState(false);

  if (!isOpen) return null;

  // Генерируем промпт для ИИ
  const generatePrompt = () => {
    // Используем динамические заголовки, если они есть, иначе fallback на статичные
    const categoryText = item.category === 'plus' 
      ? (plusListTitle && plusListTitle.trim() ? plusListTitle.trim().toLowerCase() : 'аргумент за')
      : (minusListTitle && minusListTitle.trim() ? minusListTitle.trim().toLowerCase() : 'аргумент против');
    const ratingText = item.rating === 1 ? 'очень низкая' : 
                      item.rating === 2 ? 'низкая' : 
                      item.rating === 3 ? 'средняя' : 
                      item.rating === 4 ? 'высокая' : 'очень высокая';
    
    // Начинаем с задачи
    let prompt = `Задача: ${analysisDescription}\n\n`;
    
    // Добавляем "Анализирую вопрос" только если он отличается от задачи
    if (analysisTitle && analysisTitle.trim() !== analysisDescription.trim()) {
      prompt += `Анализирую вопрос: "${analysisTitle}"\n\n`;
    }
    
    // Продолжаем с остальной частью промпта
    prompt += `Я рассматриваю ${categoryText}: "${item.text}" (оценка: ${ratingText} - ${item.rating}/5).\n\nПожалуйста, предоставь подробное объяснение этого аргумента:\n\n1. Что именно означает этот пункт в контексте анализа?\n2. Какие конкретные факторы или обстоятельства делают его ${item.category === 'plus' ? 'положительным' : 'отрицательным'}?\n3. Как этот аргумент может повлиять на общий результат анализа?\n4. Есть ли дополнительные нюансы или подводные камни, которые стоит учесть?\n5. Какие рекомендации можно дать относительно этого аргумента?\n\nОбъясни простым языком, но с достаточной детализацией.`;
    
    return prompt;
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(generatePrompt());
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Ошибка при копировании промпта:', error);
    }
  };

  const prompt = generatePrompt();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* Заголовок */}
        <div className="relative p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-3 pr-8">
            <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg flex-shrink-0">
              <Brain className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                Спросить у ИИ
              </h2>
              <p className="text-xs sm:text-sm text-gray-600">
                Промпт для получения подробного объяснения
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute top-3 sm:top-4 right-3 sm:right-4 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        {/* Контент */}
        <div className="p-3 sm:p-6 overflow-y-auto flex-1">
          {/* Информация об аргументе */}
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">
                  Анализируемый аргумент:
                </h3>
                <p className="text-sm sm:text-base text-gray-700 mb-2">{item.text}</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0 text-xs sm:text-sm text-gray-600">
                  <span className={`px-2 py-1 rounded-full w-fit ${
                    item.category === 'plus' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {item.category === 'plus' 
                      ? (plusListTitle && plusListTitle.trim() ? plusListTitle.trim() : 'Плюс')
                      : (minusListTitle && minusListTitle.trim() ? minusListTitle.trim() : 'Минус')}
                  </span>
                  <span>Оценка: {item.rating}/5</span>
                </div>
              </div>
            </div>
          </div>

          {/* Промпт */}
          <div className="mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2 sm:gap-0">
              <h3 className="font-medium text-gray-900 text-sm sm:text-base">Сгенерированный промпт:</h3>
              <button
                onClick={handleCopyPrompt}
                className="flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm self-start sm:self-auto"
              >
                {isCopied ? (
                  <>
                    <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Скопировано!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Копировать</span>
                  </>
                )}
              </button>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
              <pre className="whitespace-pre-wrap text-xs sm:text-sm text-gray-800 font-mono leading-relaxed">
                {prompt}
              </pre>
            </div>
          </div>

          {/* Инструкция */}
          <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2 text-sm sm:text-base">Как использовать:</h4>
            <ol className="text-xs sm:text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Скопируйте промпт кнопкой выше</li>
              <li>Вставьте его в любой ИИ-сервис (ChatGPT, Claude, Gemini и т.д.)</li>
                              <li>Получите подробное объяснение выбранного аргумента</li>
            </ol>
          </div>
        </div>

        {/* Кнопки */}
        <div className="flex items-center justify-end space-x-3 p-3 sm:p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm sm:text-base text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
} 